'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import type { SxProps, Theme } from '@mui/system';
import { TypingIndicator } from '@mui/x-chat-headless';
import type { TypingIndicatorProps, TypingIndicatorSlots } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from '../internals/zero-styled';
import { mergeSlotProps } from '../internals/mergeSlotProps';
import { useChatTypingIndicatorUtilityClasses } from './chatTypingIndicatorClasses';
import type { ChatTypingIndicatorClasses } from './chatTypingIndicatorClasses';

const useThemeProps = createUseThemeProps('MuiChatTypingIndicator');

export interface ChatTypingIndicatorSlots extends TypingIndicatorSlots {}

export interface ChatTypingIndicatorProps extends TypingIndicatorProps {
  className?: string;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps<Theme>;
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<ChatTypingIndicatorClasses>;
}

const ChatTypingIndicatorStyled = styled('div', {
  name: 'MuiChatTypingIndicator',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root,
})(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1.5),
  fontSize: theme.typography.caption.fontSize,
  color: (theme.vars || theme).palette.text.secondary,
  fontStyle: 'italic',
}));

const ChatTypingIndicator = React.forwardRef<HTMLDivElement, ChatTypingIndicatorProps>(
  function ChatTypingIndicator(inProps, ref) {
    const props = useThemeProps({ props: inProps, name: 'MuiChatTypingIndicator' });
    const { slots, slotProps, className, classes: classesProp, sx, ...other } = props;
    const classes = useChatTypingIndicatorUtilityClasses(classesProp);

    return (
      <TypingIndicator
        ref={ref}
        {...other}
        slots={{
          ...slots,
          root: slots?.root ?? ChatTypingIndicatorStyled,
        }}
        slotProps={{
          ...slotProps,
          root: mergeSlotProps(
            {
              className: clsx(classes.root, className),
              sx,
            },
            slotProps?.root,
          ) as any,
        }}
      />
    );
  },
);

ChatTypingIndicator.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  className: PropTypes.string,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
    PropTypes.func,
    PropTypes.object,
  ]),
} as any;

export { ChatTypingIndicator };
